import { css } from 'styled-components';

import { desktop, mobile } from '../../../../techstyle-shared/react-components';
import { Select } from '../../../../techstyle-shared/react-components-legacy';
import { transformStylesToVariants } from '../utils/transformStylesToVariants';

const getButtonBackgroundColor = ({ theme, ...props }) => {
  if (props.secondary) {
    return 'transparent';
  }
  if (props.alternate) {
    return theme.colors.palette.lavender;
  }
  return props.white
    ? theme.colors.palette.white
    : theme.colors.palette.gunmetal;
};

const getButtonBorderColor = ({ theme, ...props }) => {
  if (props.alternate) {
    return theme.colors.palette.lavender;
  }
  if (props.secondary && props.white) {
    return theme.colors.palette.white;
  }
  return props.white
    ? theme.colors.palette.white
    : theme.colors.palette.gunmetal;
};

const getButtonFontColor = ({ theme, ...props }) => {
  if (props.secondary) {
    return props.white
      ? theme.colors.palette.white
      : theme.colors.palette.gunmetal;
  }
  return props.white
    ? theme.colors.palette.gunmetal
    : theme.colors.palette.white;
};

const defaultDisabledButtonStyle = css`
  opacity: 50%;
  cursor: not-allowed;
`;

const getButtonHoverBackgroundColor = ({ theme, ...props }) => {
  if (props.secondary) {
    return 'transparent';
  }
  if (props.alternate) {
    return theme.colors.palette.lavender400;
  }
  return props.white ? theme.colors.palette.white : theme.colors.palette.black;
};

const sharedButtonStyles = css`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.palette.gunmetal};
  background-color: ${getButtonBackgroundColor};
  border: 1px solid ${getButtonBorderColor};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.palette.white};
  color: ${getButtonFontColor};
  cursor: pointer;
  display: inline-flex;
  min-height: 50px;
  justify-content: center;
  margin: 0;
  min-width: 182px;
  outline: 0;
  padding: 16px 24px;
  text-align: center;
  text-decoration: none;
  user-select: none;
  vertical-align: middle;
  width: ${(props) => (props.fullWidth ? '100%' : 'initial')};
  transition: background-color 300ms linear, border-color 300ms linear;

  ${({ theme }) => theme.typography.values.subHeading3Uppercase}
  &:disabled {
    ${defaultDisabledButtonStyle};
  }

  /* Apply disabled styles for ButtonLink */
  ${(props) =>
    props.disabled && props.tagName !== 'button'
      ? defaultDisabledButtonStyle
      : ''};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.palette.lavender500};
  }

  &:hover {
    background-color: ${getButtonHoverBackgroundColor};
  }

  svg {
    margin-right: 5px;
    margin-bottom: 2px;
    /* Horizontally center the content better. */
    margin-left: -5px;
  }
`;

const sharedPrimaryButtonStyles = css`
  min-width: 186px;
  padding: 16px 24px;
  border: none;
`;
const sharedSecondaryButtonStyles = css`
  min-width: 208px;
  padding: 16px 24px;
  border: 1px solid;
  background-color: transparent;
`;
const sharedTertiaryButtonStyles = css`
  min-width: 167px;
  padding: 8px 16px;
  min-height: 32px;
  border: 0;
  ${({ theme }) => theme.typography.values.subHeading4Uppercase}
`;
const buttonVariants = {
  default: sharedButtonStyles,
  primaryDark: css`
    ${sharedButtonStyles}
    ${sharedPrimaryButtonStyles}
    background-color: ${({ theme }) => theme.colors.palette.gunmetal};
    color: ${({ theme }) => theme.colors.palette.white};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.black};
    }
  `,
  primaryLight: css`
    ${sharedButtonStyles}
    ${sharedPrimaryButtonStyles}
    background-color: ${({ theme }) => theme.colors.palette.white};
    color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.stone};
    }
  `,
  primaryLavender: css`
    ${sharedButtonStyles}
    ${sharedPrimaryButtonStyles}
    background-color: ${({ theme }) => theme.colors.palette.lavender};
    color: ${({ theme }) => theme.colors.palette.black};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.lavender400};
    }
  `,
  secondaryDark: css`
    ${sharedButtonStyles}
    ${sharedSecondaryButtonStyles}
    color: $${({ theme }) => theme.colors.palette.gunmetal};
    border-color: ${({ theme }) => theme.colors.palette.black};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.stone_50};
    }
  `,
  secondaryLight: css`
    ${sharedButtonStyles}
    ${sharedSecondaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.white};
    border-color: ${({ theme }) => theme.colors.palette.white};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.gunmetal_50};
    }
  `,
  secondaryLavender: css`
    ${sharedButtonStyles}
    ${sharedSecondaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.lavender600};
    border-color: ${({ theme }) => theme.colors.palette.lavender600};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.lavender100};
      border-color: ${({ theme }) => theme.colors.palette.lavender700};
    }
  `,
  tertiaryDark: css`
    ${sharedButtonStyles}
    ${sharedTertiaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.white};
    background-color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.black};
    }
  `,
  tertiaryLight: css`
    ${sharedButtonStyles}
    ${sharedTertiaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.gunmetal};
    background-color: ${({ theme }) => theme.colors.palette.white};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.stone};
    }
  `,
  tertiaryDarkOutline: css`
    ${sharedButtonStyles}
    ${sharedTertiaryButtonStyles}
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.palette.black};
    color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.stone_50};
    }
  `,
};

const headerVariants = {
  h1: css`
    font-family: 'Arizona';
    font-size: 90px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 2px;
    text-transform: uppercase;
  `,
  h1Alt: css`
    font-family: 'Arizona';
    font-size: 45px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 1px;
    text-transform: uppercase;
  `,
  h2: css`
    font-family: 'Arizona';
    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 1px;
    text-transform: uppercase;
  `,
  h3: css`
    font-family: 'Arizona';
    font-size: 20px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 1px;
    text-align: left;
    text-transform: uppercase;
  `,
};

export const themeTokens = {
  globalStyles: {
    button: css`
      border: 0;
      border-radius: 0;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
      background: transparent;
      color: inherit;
      cursor: pointer;
    `,
  },
  checkoutDefaultLayout: {
    wrapper: css`
      font-size: 14px;
      line-height: ${20 / 14};

      ${desktop`
        background: ${({ theme }) => theme.colors.palette.marble};
      `}
    `,
  },
  logoAndPageNameHeader: {
    wrapper: css`
      background-color: ${({ theme }) => theme.colors.palette.white};
    `,
    styledLink: css`
      padding: ${(props) => props.theme.spacing.sizes.pixels.xs}px 0
        ${(props) => props.theme.spacing.sizes.pixels.xs}px;
      gap: ${(props) => props.theme.spacing.sizes.pixels.s}px;
    `,
    pageNameWrapper: css`
      ${({ theme }) => theme.typography.values.subHeading2Uppercase}
      line-height: unset;
    `,
  },
  cart: {
    styledCountdownTimerHeader: css`
      margin-bottom: ${(props) =>
        props.isGiftPromoEnabled ? 0 : props.theme.spacing.sizes.pixels.s}px;
    `,
  },
  errorInputV2: {
    defaultStyle: css`
      margin: 4px 0 20px 0;
      font-size: 12px;
      line-height: ${72 / 5}px;
      color: ${({ theme }) => theme.colors.palette.red500};
    `,
  },
  inputV2: {
    defaultStyle: css`
      padding: 2px 15px 0 0;
      line-height: 21px;
      transform: translate3d(0, 2px, 0);

      ${desktop`
        font-size: 14px;
      `}
      &.focus-visible,
      &:focus-visible {
        outline: none !important;
      }

      &[data-label-position='inside'] {
        border-bottom: solid 10px ${({ theme }) => theme.colors.palette.red500};
      }
    `,
    inputErrorStyle: css`
      padding: 2px 15px 0 0;
      line-height: 21px;
      transform: translate3d(0, 2px, 0);

      ${desktop`
        font-size: 14px;
      `}
      &.focus-visible,
      &:focus-visible {
        outline: none !important;
      }
    `,
  },
  inputBoxV2: {
    defaultStyle: css`
      border-color: ${({ theme }) => theme.colors.palette.gunmetal};
      border-style: solid;
      border-width: 1px;
      padding: 6px 16px 8px 16px;

      label::first-letter {
        text-transform: capitalize;
      }

      label[data-label-position='inside'],
      div[data-label-position='inside'] {
        color: ${({ theme }) => theme.colors.palette.gunmetal};
      }

      &[data-has-focus],
      &[data-has-value] {
        padding: 6px 16px 8px 16px;
        border-width: 1px;

        ~ p[data-field-hint] {
          color: ${({ theme }) => theme.colors.palette.gunmetal};
        }
      }

      [data-after-input],
      [data-before-input] {
        margin: 0;
      }

      &[data-has-focus] {
        border-color: ${({ theme }) => theme.colors.palette.black};

        label[data-label-position='inside'],
        div[data-label-position='inside'] {
          color: ${({ theme }) => theme.colors.palette.black};
        }
      }
    `,

    inputBoxErrorStyle: css`
      border-color: ${({ theme }) => theme.colors.palette.red500};
      border-style: solid;
      border-width: 1px;
      padding: 6px 16px 8px 16px;

      label::first-letter {
        text-transform: capitalize;
      }

      &[data-has-focus] {
        padding: 6px 16px 8px 16px;
        border-color: ${({ theme }) => theme.colors.palette.red500};
        border-width: 1px;
      }

      [data-after-input],
      [data-before-input] {
        margin: 0;
      }
    `,
    adyenStyle: css`
      border-color: ${({ theme, ...props }) =>
        props.hasPaymentError
          ? theme.colors.palette.red500
          : theme.colors.palette.gunmetal};
    `,
  },
  labelInputV2: {
    defaultStyle: css`
      margin: 0;

      &[data-label-position='floating'],
      &[data-label-position='inside'] {
        margin-left: 0;
      }

      &[data-label-position='inside'] {
        font-size: 12px;
        color: ${({ theme }) => theme.colors.palette.gray500};
        transform: translate3d(0, 0, 0) scale(1);
      }

      &[data-label-position='floating'] {
        font-size: 14px;
        transform: translate3d(0, 12px, 0);
        margin: 0;
        line-height: 1;
        color: ${({ theme }) => theme.colors.palette.gray500};
      }
    `,
    labelInputErrorStyle: css`
      margin: 0;

      &[data-label-position='floating'],
      &[data-label-position='inside'] {
        margin-left: 0px;
      }

      &[data-label-position='inside'] {
        font-size: 12px;
        color: ${({ theme }) => theme.colors.palette.red500};
        transform: translate3d(0, 0, 0) scale(1);
        margin-left: 0;
      }

      &[data-label-position='floating'] {
        color: ${({ theme }) => theme.colors.palette.gray500};
        font-size: 14px;
        transform: translate3d(0, 12px, 0);
        margin: 0;
        line-height: 1;
      }
    `,
  },
  spacing: {
    none: 0,
    tiny: 8,
    small: 16,
    moderate: 24,
    medium: 32,
    large: 40,
    xlarge: 80,
  },
  button: {
    defaultStyle: sharedButtonStyles,
    variants: transformStylesToVariants(buttonVariants, 'buttonStyle'),
  },
  collapsibleTrigger: {
    defaultStyle: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 12px;
      font-weight: bold;
      line-height: ${32 / 12};
      padding: 8px 0;
      cursor: pointer;
      user-select: none;
    `,
    collapsibleButton: css`
      background: ${(props) => props.theme.colors.palette.white};
      padding: 0 ${(props) => props.theme.spacing.sizes.pixels.s}px;
      border-top: 1px solid ${(props) => props.theme.colors.palette.gray250};
      border-bottom: 1px solid ${(props) => props.theme.colors.palette.gray250};
    `,
  },
  inputBox: {
    defaultStyle: css`
      border-color: ${({ theme }) => theme.colors.palette.black};
      border-style: none none solid;
      border-width: 0px 0px 2px;
      border-image: none 100% / 1 / 0 stretch;
      border-radius: 0px;
      background: transparent none repeat scroll 0% 0%;
      padding: 0;

      &[data-has-focus] {
        padding: 0;
        border-width: 0px 0px 2px;
        border-style: none none solid;
        box-shadow: none;
        border-color: ${({ theme }) => theme.colors.palette.black};

        label[data-label-position='inside'],
        div[data-label-position='inside'] {
          color: ${({ theme }) => theme.colors.palette.lavender500};
        }
      }
    `,
    inputBoxErrorStyle: css`
      border-color: ${({ theme }) => theme.colors.palette.red500};
      border-style: none none solid;
      border-width: 0px 0px 2px;
      border-image: none 100% / 1 / 0 stretch;
      border-radius: 0px;
      background: transparent none repeat scroll 0% 0%;
      padding: 0;

      &[data-has-focus] {
        padding: 0;
        border-width: 0px 0px 2px;
        border-style: none none solid;
        box-shadow: none;
        border-color: ${({ theme }) => theme.colors.palette.lavender500};

        label[data-label-position='inside'],
        div[data-label-position='inside'] {
          color: ${({ theme }) => theme.colors.palette.lavender500};
        }
      }
    `,
  },
  input: {
    defaultStyle: css`
      background: transparent none repeat scroll 0% 0%;
      padding: 0px 15px 8px 0px;
      line-height: 21px;

      ${desktop`
        font-size: 14px;
      `}
      &.focus-visible,
      &:focus-visible {
        outline: none !important;
      }

      &[data-label-position='inside'] {
        border-bottom: solid 10px ${({ theme }) => theme.colors.palette.red500};
      }
    `,
    inputErrorStyle: css`
      background: transparent none repeat scroll 0% 0%;
      padding: 0px 15px 8px 0px;
      line-height: 21px;

      ${desktop`
        font-size: 14px;
      `}
      &.focus-visible,
      &:focus-visible {
        outline: none !important;
      }
    `,
  },
  labelInput: {
    defaultStyle: css`
      margin: 0;
      text-transform: uppercase;

      &[data-label-position='floating'],
      &[data-label-position='inside'] {
        margin-left: 0px;
        line-height: 21px;
      }

      &[data-label-position='inside'] {
        font-size: 10px;
        color: ${({ theme }) => theme.colors.palette.gray500};
        transform: translate3d(0, 0, 0) scale(1);
        margin: 0;
      }

      &[data-label-position='floating'] {
        font-size: 14px;
        transform: translate3d(0, 100%, 0);
        margin: 0;
      }
    `,
    labelInputErrorStyle: css`
      margin: 0;
      text-transform: uppercase;

      &[data-label-position='floating'],
      &[data-label-position='inside'] {
        margin-left: 0px;
        line-height: 21px;
      }

      &[data-label-position='inside'] {
        font-size: 10px;
        color: ${({ theme }) => theme.colors.palette.red500};
        transform: translate3d(0, 0, 0) scale(1);
        margin: 0;
      }

      &[data-label-position='floating'] {
        color: ${({ theme }) => theme.colors.palette.red500};
        font-size: 14px;
        transform: translate3d(0, 100%, 0);
        margin: 0;
      }
    `,
  },
  errorInput: {
    defaultStyle: css`
      min-height: 15px;
      font-size: 10px;
      line-height: 1.5;
      color: ${({ theme }) => theme.colors.palette.red500};
    `,
  },
  joinVipAnnual: {
    shared: {
      styledText: css`
        color: ${(props) => props.theme.colors.palette.lavender700};

        ${({ allCaps, theme }) =>
          allCaps
            ? theme.typography.values.subHeading8Uppercase
            : theme.typography.values.subHeading8}

        ${({ allCaps }) =>
          allCaps &&
          css`
            line-height: 1.5;
          `}
      `,
      learnMoreLink: css`
        & > span {
          color: ${(props) => props.theme.colors.palette.lavender700};

          &:hover,
          &:focus,
          &:active {
            color: ${(props) => props.theme.colors.palette.lavender700};
            text-decoration: none;
          }
        }
      `,
    },
  },
  basketItemPrice: {
    basketPrice: css`
      ${({ theme }) => theme.typography.values.subHeading8Uppercase}

      line-height: 1.3;

      color: ${(props) => props.isClearanceItem && props.theme.colors.error};

      color: ${(props) =>
        !props.isClearanceItem && props.special
          ? props.theme.colors.palette.gunmetal
          : 'inherit'};

      ${({ shoppingBagFlagStatus, theme, special, old }) =>
        !shoppingBagFlagStatus?.isControl &&
        theme.context.cartDrawer &&
        css`
          ${special &&
          `
          font-weight: 600;
          color: ${theme.colors.palette.black};
        `}

          ${old && `color: ${theme.colors.palette.gunmetal600};`}
        `}
    `,
    priceLabel: css`
      ${({ theme }) => theme.typography.values.subHeading8Uppercase}

      line-height: 1.5;
    `,
  },
  basketItemDetails: {
    basketPricePromo: css`
      color: ${(props) =>
        props.isClearanceItem
          ? props.theme.colors.error
          : props.theme.colors.palette.lavender700};

      ${({ theme }) => theme.typography.values.subHeading8Uppercase}

      line-height: 1.3;
    `,
  },
  basketItem: {
    productName: css`
      ${({ theme }) => theme.typography.values.subHeading8Uppercase}

      line-height: 1.3;
    `,
  },
  basicModal: {
    dialogStyle: css`
      background-color: ${({ theme }) => theme.colors.palette.white};
    `,
  },
  productDetailName: {
    productName: css`
      ${({ theme }) => theme.typography.values.heading6Uppercase}

      line-height: 1.2;
    `,
  },
  productDetailHeader: {
    promoTextWrapper: css`
      color: ${({ theme }) => theme.colors.memberOfferText};
      ${({ theme }) => theme.typography.values.subHeading8Uppercase};
      font-weight: 400;
      line-height: 1.5;
    `,
  },
  productDetailSizeSelector: {
    sizeCategoryLabel: css`
      color: ${({ theme }) => theme.colors.palette.gunmetal600};

      ${({ theme }) =>
        theme.typography.variants.checkout.itemInformation.textStyle}
    `,
  },
  colorSwatch: {
    colorTitle: css`
      color: ${({ theme }) => theme.colors.palette.gunmetal600};
    `,
  },
  confirmationButton: css`
    border-radius: 0px;

    & > span {
      font-size: 14px;
    }
  `,
  quickViewModal: {
    styledLink: css`
      ${({ theme }) => theme.typography.values.subHeading6smallUppercase}
      line-height: 1.3;
      color: ${({ theme }) => theme.colors.palette.lavender600};
      border: 1px solid ${({ theme }) => theme.colors.palette.lavender600};
      background-color: transparent;
      transition: background-color 300ms linear, border-color 300ms linear;

      &:hover {
        background-color: ${({ theme }) => theme.colors.palette.lavender100};
        border-color: ${({ theme }) => theme.colors.palette.lavender700};
      }
    `,
  },
  selectV2: {
    errorStyle: css`
      margin-bottom: 18px;

      [data-button-list-wrapper] {
        border-color: ${({ theme }) => theme.colors.palette.red500};
        color: ${({ theme }) => theme.colors.palette.red500};
        border-style: solid;
        border-width: 1px;
      }

      select {
        border-color: ${({ theme }) => theme.colors.palette.red500};
      }

      ${Select.Label} {
        color: ${({ theme }) => theme.colors.palette.red500};
      }
    `,
    errorHintStyle: css`
      font-size: 12px;
      line-height: 14.4px;
      color: ${({ theme }) => theme.colors.palette.red500};
    `,
    listStyle: css`
      background-color: ${({ theme }) => theme.colors.palette.white};
      font-size: 14px;
      min-width: 100%;
      position: absolute;
      left: 0;
      z-index: 100;
      max-height: 252px;
      overflow-y: auto;

      ${desktop`
        font-size: 12px;
        left: auto;
        right: 0;
      `}
    `,
    itemStyle: css`
      height: 42px;
      cursor: pointer;
      padding: 16px;
      white-space: nowrap;
      color: ${({ theme }) => theme.colors.palette.gunmetal};
      font-size: 14px;
      line-height: 8px;
      font-weight: 400;
      border-width: 0px 1px 1px 1px;
      border-style: solid;
      border-color: ${({ theme }) => theme.colors.palette.black};

      &:first-child {
        border-width: 1px 1px 1px 1px;
      }

      &:hover,
      &:checked,
      &[data-selected='true'] {
        background-color: ${({ theme }) => theme.colors.palette.gunmetal};
        color: ${({ theme }) => theme.colors.palette.white};
      }
    `,
    buttonStyle: css`
      padding: 25px 16px 8px 16px;
      color: ${({ theme }) => theme.colors.palette.black};
    `,
    labelStyle: css`
      position: absolute;
      top: 4px;
      left: ${({ theme }) => theme.checkout.spacing.small}px;
      text-transform: none;
      font-size: 12px;
      color: ${({ theme }) => theme.colors.palette.gunmetal};
    `,
    wrapperStyle: css`
      flex-grow: 1;
      position: relative;
      width: 100%;
      border-color: ${({ theme }) => theme.colors.palette.gunmetal};
      border-style: solid;
      border-width: 1px;
    `,
    quizStyle: css`
      select,
      select:focus {
        border-color: ${({ theme }) => theme.colors.palette.white};
        color: ${({ theme }) => theme.colors.palette.white};
        background-color: transparent;

        ~ svg {
          color: ${({ theme }) => theme.colors.palette.white} !important;
        }

        ~ ${Select.Label} > span {
          color: ${({ theme }) => theme.colors.palette.white};
        }
      }

      label {
        background-color: transparent;
      }

      ${Select.Label} {
        transition: top 0.1s ease-out 0s;
      }
    `,
    defaultStyle: css`
      ${Select.Caret} {
        right: 16px;
        bottom: 0;
        top: calc(50% - 2px);
        transition: all 0.2s ease-out 0s;
        fill: ${({ theme }) => theme.colors.palette.gunmetal};
      }

      select,
      select:focus {
        padding: 22px 16px 8px 16px;
        border-color: ${({ theme }) => theme.colors.palette.gunmetal};
        border-style: solid;
        border-width: 1px;
        font-size: 14px;
      }

      select:focus {
        border-color: ${({ theme }) => theme.colors.palette.black};

        ~ svg {
          color: ${({ theme }) => theme.colors.palette.black} !important;
        }

        ~ ${Select.Label} {
          color: ${({ theme }) => theme.colors.palette.black};
        }
      }

      ${desktop`
        select:active ~ svg {
          transform: rotate(180deg);
        }
      `}
      svg {
        right: 16px;
        bottom: 0;
        top: calc(50% - 2px);
      }

      ${Select.Label} {
        position: absolute;
        top: 4px;
        left: ${({ theme }) => theme.checkout.spacing.small}px;
        text-transform: none;
        font-size: 12px;
      }

      ${Select.Footer} {
        margin: 2px 0;
        min-height: ${({ theme }) => theme.checkout.spacing.small}px;
        font-size: 12px;
      }

      ${Select.Error} {
        line-height: 16px;
      }

      label {
        background-color: ${({ theme }) => theme.colors.palette.white};
      }
    `,
  },
  countdownTimerHeader: {
    countdownTimerWrapper: css`
      color: ${({ theme }) => theme.colors.semanticColors.default};
      background: ${({ theme }) => theme.colors.palette.lavender300};
    `,
    styledCountdown: css`
      padding: 0 0 0 ${(props) => props.theme.checkout.spacing.tiny}px;
    `,
  },
  hintInputV2: {
    defaultStyle: css`
      margin: 4px 0 20px 0;
      font-size: 12px;
      line-height: 1.25;
      color: ${({ theme }) => theme.colors.palette.gunmetal600};
    `,
  },
  header: {
    variants: transformStylesToVariants(headerVariants, 'textStyles'),
  },
  promoCode: {
    styledApplyButton: css`
      background-color: ${({ showSuccess, theme }) =>
        showSuccess
          ? theme.colors.palette.black
          : theme.colors.palette.gunmetal600};

      border: 1px solid ${({ theme }) => theme.colors.palette.gunmetal600};

      height: 44px;
      min-height: 50px;

      & > span {
        ${({ theme }) => theme.typography.values.paragraph3Uppercase};

        line-height: 1.25;
      }
    `,
  },
  totalPriceSummary: {
    subHeader: css`
      ${({ total }) =>
        total
          ? css`
              ${({ theme }) => theme.typography.values.subHeading8Uppercase}
              line-height: 1.3;
            `
          : css`
              ${({ theme }) => theme.typography.values.paragraph2Uppercase}
              line-height: 1.5;
            `}
    `,
    shippingAndTaxesDisclaimer: css`
      ${({ theme }) => theme.typography.values.paragraph1Uppercase}
      line-height: 1.4;
    `,
  },
  cartUpsellCarousel: {
    title: css`
      ${(props) =>
        props.isVariantRevampedSB
          ? props.theme.typography.values.heading6Uppercase
          : props.theme.typography.values.subHeading6smallUppercase}

      line-height: 1.3;
    `,
    styledArrowButton: css`
      &:disabled {
        color: ${(props) => props.theme.colors.palette.gray200};
        cursor: not-allowed;
      }
    `,
  },
  productCellBmig: {
    quickShopLabel: css`
      ${({ theme }) => theme.typography.values.paragraph2Uppercase};
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 1.6px;

      color: ${({ theme }) => theme.colors.palette.white};
      background-color: ${({ theme }) => theme.colors.palette.gunmetal};
      transition: background-color 300ms linear, border-color 300ms linear;

      &:hover {
        background-color: ${({ theme }) => theme.colors.palette.black};
      }
    `,
    productName: css`
      ${({ theme }) => theme.typography.values.paragraph2Uppercase}
      font-weight: 400;
      line-height: 1.5;
      color: ${(props) => props.theme.colors.palette.black};

      margin: 4px 0 ${(props) => props.theme.spacing.sizes.pixels.xxs}px 0;
    `,
  },
  memberCreditsCartToggleNmp: {
    textStyled: css`
      ${({ theme }) => theme.typography.values.subHeading8Uppercase}
      line-height: 1.35;
      color: ${({ theme }) => theme.colors.palette.black};
    `,
  },
  memberCreditBenefitsModal: {
    title: css`
      color: ${(props) => props.theme.colors.palette.black};
      ${({ theme }) => theme.typography.values.subHeading7smallUppercase}
      line-height: 1.25;

      padding: ${(props) => props.theme.spacing.sizes.pixels.m}px 0
        ${(props) => props.theme.spacing.sizes.pixels.s}px;
    `,
    item: css`
      color: ${(props) => props.theme.colors.semanticColors.default};
      ${({ theme }) => theme.typography.values.paragraph3}
      line-height: 1.45;

      margin-bottom: ${(props) => props.theme.spacing.sizes.pixels.s}px;
    `,
    buttonLink: css`
      ${({ theme }) => theme.checkout.button.variants.primaryLight.buttonStyle}

      border-radius: 0px;

      & > span {
        color: ${({ theme }) => theme.colors.palette.gunmetal};
        ${({ theme }) => theme.typography.values.paragraph3}
        line-height: 1.3;
      }
    `,
    faQuestionCircleStyled: css`
      color: ${({ theme }) => theme.colors.palette.lavender500};

      ${({ theme, isAfter }) =>
        isAfter && `margin-left: ${theme.spacing.sizes.pixels.s}px;`}

      ${({ theme, isShoppingBagRevampUX }) =>
        isShoppingBagRevampUX && `color: ${theme.colors.memberOfferText};`}
    `,
  },
  memberCreditsCartToggleButton: {
    toggleButtonStyled: {
      applied: css`
        background: ${({ isShoppingBagRevampUX, theme }) =>
          isShoppingBagRevampUX
            ? theme.colors.palette.lavender
            : theme.colors.semanticColors.promo};
      `,
      unavailable: css`
        cursor: default;
        background: ${({ isShoppingBagRevampUX, theme }) =>
          isShoppingBagRevampUX
            ? theme.colors.palette.stone
            : theme.colors.palette.gray400};
      `,
      default: css`
        background: ${({ isShoppingBagRevampUX, theme }) =>
          isShoppingBagRevampUX
            ? theme.colors.palette.stone
            : theme.colors.palette.gray500};
      `,
    },
  },
  orderDetails: {
    field: css`
      ${({ theme }) => theme.typography.values.paragraph3Uppercase};
      font-weight: 400;
      line-height: 1.3;

      color: ${({ theme }) => theme.colors.palette.gunmetal};

      > a {
        ${({ theme }) => theme.typography.values.paragraph3Uppercase};
        color: ${({ theme }) => theme.colors.palette.gunmetal};

        border-bottom: 1px solid ${({ theme }) => theme.colors.palette.black};
        padding-bottom: 2px;
      }

      ${({ hasTitle, theme }) =>
        hasTitle &&
        `
          ${theme.typography.values.paragraph3};
          margin-bottom: ${theme.spacing.sizes.pixels.s}px;
      `}
    `,
    nestedTitle: css`
      ${({ hasTitle, theme }) =>
        `
        color: ${theme.colors.palette.gunmetal};
        opacity: ${hasTitle ? '1' : '0.5'};
      `};
    `,
  },
  klarnaMessaging: {
    klarnaMessagingWrapper: css`
      ${({ theme }) => theme.typography.values.paragraph1}
      line-height: 1.8;
      color: ${(props) => props.theme.colors.palette.gunmetal};

      margin-top: ${(props) => props.theme.spacing.sizes.pixels.xxs}px;
      margin-left: -${(props) => props.theme.spacing.sizes.pixels.xxl}px;
      padding: 4px ${(props) => props.theme.spacing.sizes.pixels.xxs}px 4px
        ${(props) => props.theme.spacing.sizes.pixels.xxl}px;
    `,
  },
  trustPilotMessage: {
    checkoutSection: {
      trustPilotCheckoutSection: css`
        background-image: linear-gradient(
          to bottom,
          ${({ theme }) => theme.colors.palette.lavender200},
          ${({ theme }) => theme.colors.palette.lavender100}
        );

        border: solid 1px ${({ theme }) => theme.colors.palette.lavender300};
        margin-top: ${({ theme }) => theme.spacing.sizes.pixels.m}px;

        ${mobile`
          margin: ${({ theme }) => theme.spacing.sizes.pixels.m}px ${({
          theme,
        }) => theme.spacing.sizes.pixels.s}px ${({ theme }) =>
          theme.spacing.sizes.pixels.m}px;
        `}
      `,
      title: css`
        border-bottom: 1px solid
          ${({ theme }) => theme.colors.palette.lavender300};
      `,
    },
    poweredByText: css`
      ${({ theme }) => theme.typography.values.paragraph1Uppercase};
      font-weight: 500;
      line-height: 1.4;
      color: ${(props) => props.theme.colors.palette.gray500};

      margin-bottom: ${(props) => props.theme.spacing.sizes.pixels.s}px;
    `,
  },
  checkoutSection: {
    title: css`
      ${(props) => props.theme.typography.values.paragraph3Uppercase};
      line-height: 1.4;
    `,
  },
};
